import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';
import { useToastStore } from '../../../stores/toast-store';

/**
 * useClaimConversation — Lote Claim.
 *
 * Mutation para o atendente assumir (claim) uma conversa não-atribuída. On
 * success invalida o detalhe (banner some + UI reflete posse) + a listagem
 * (conversa migra de "Todas" para "Minhas"). Espelho de `useSetOperationMode`.
 *
 * Capability gate backend: `core-conversation`. Corrida entre atendentes →
 * 409 (toast de erro).
 */
export function useClaimConversation(conversationId: string | undefined): {
  claim: () => Promise<void>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      if (conversationId === undefined) {
        throw new Error('conversationId obrigatório para assumir conversa');
      }
      return api.claimConversation(conversationId);
    },
    onSuccess: () => {
      if (conversationId === undefined) return;
      void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      useToastStore.getState().push({
        variant: 'success',
        title: 'Conversa assumida',
        description: 'Você agora é o atendente — sugestões da IA habilitadas.',
        durationMs: 3500,
      });
    },
    onError: (err) => {
      useToastStore.getState().push({
        variant: 'error',
        title: 'Falha ao assumir conversa',
        description: err instanceof Error ? err.message : 'Erro desconhecido',
        durationMs: 6000,
      });
    },
  });

  return {
    claim: async () => {
      await mutation.mutateAsync();
    },
    isPending: mutation.isPending,
  };
}
